import React from 'react';

const AUTHOR_REGEX = /^(Maxime|C\.S\.|Augustin|Thomas|Dostoïevski|Thérèse)/;

/** Matches: **Auteur** — « citation » */
const AUTHOR_QUOTE_REGEX = /^\*\*(.+?)\*\*\s*[—–]\s*«(.+)»\s*$/;

/** Renders a plain text segment with **bold** and *italic* markdown support */
export const renderBold = (text: string, keyPrefix: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={`${keyPrefix}-i${i}`}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>;
  });
};

/** Injecte le locale dans une URL bible4.me si elle n'en a pas déjà un. */
const rewriteBibleUrl = (url: string, locale: string): string => {
  if (/^https:\/\/bible4\.me\/[a-z]{2}\//.test(url)) return url;
  return url.replace('https://bible4.me/', `https://bible4.me/${locale}/`);
};

const renderLineWithLinks = (line: string, locale: string) => {
  const parts = line.split(/(https:\/\/bible4\.me\/\S+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('https://bible4.me/')) {
      const href = rewriteBibleUrl(part, locale);
      return (
        <a
          key={index}
          href={href}
          className="text-amber-600 font-semibold no-underline"
          target="_blank"
          rel="noreferrer"
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{renderBold(part, String(index))}</React.Fragment>;
  });
};

interface ContentRendererProps {
  text?: string;
  locale?: string;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ text, locale = 'fr' }) => {
  const lines = (text || '').split('\n');

  return (
    <div>
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={index} className="h-2" />;
        }

        if (trimmed.includes('https://bible4.me/')) {
          return (
            <p key={index} className="my-1 text-base text-amber-900/90 leading-relaxed">
              {renderLineWithLinks(trimmed, locale)}
            </p>
          );
        }

        const authorQuoteMatch = trimmed.match(AUTHOR_QUOTE_REGEX);
        if (authorQuoteMatch) {
          const [, author, citation] = authorQuoteMatch;
          return (
            <blockquote
              key={index}
              className="my-2 border-l-[3px] border-amber-600 pl-3 leading-relaxed"
            >
              <span className="block text-sm font-bold text-amber-700 not-italic mb-0.5">
                {author}
              </span>
              <span className="text-base italic text-amber-800">«{citation}»</span>
            </blockquote>
          );
        }

        if (trimmed.startsWith('«')) {
          return (
            <blockquote
              key={index}
              className="my-2 border-l-[3px] border-amber-600 pl-3 text-base italic text-amber-800 leading-relaxed"
            >
              {renderBold(trimmed, String(index))}
            </blockquote>
          );
        }

        if (AUTHOR_REGEX.test(trimmed)) {
          return (
            <p
              key={index}
              className="mt-2 mb-0.5 text-sm font-bold italic text-amber-700"
            >
              {renderBold(trimmed, String(index))}
            </p>
          );
        }

        if (trimmed.startsWith('❓') || trimmed.startsWith('> ')) {
          const content = trimmed.startsWith('> ') ? trimmed.slice(2) : trimmed;
          return (
            <p
              key={index}
              className="my-3 text-base font-semibold text-amber-900 leading-relaxed bg-amber-100 border border-amber-200 rounded-lg px-3 py-2"
            >
              {renderBold(content, String(index))}
            </p>
          );
        }

        return (
          <p key={index} className="my-1 text-base text-amber-900/90 leading-relaxed">
            {renderBold(trimmed, String(index))}
          </p>
        );
      })}
    </div>
  );
};

export default ContentRenderer;
